import React from 'react';
import { Alert, Button, Card, Col, Form, Row, Spinner } from 'react-bootstrap';
import { ArrowRepeat, People, Person, Receipt } from 'react-bootstrap-icons';
import './webhooks.css';
import {
  activateDynamicsContactWebhook,
  activateDynamicsCustomerWebhook,
  activateDynamicsLedgerWebhook,
  getDynamicsWebhooks,
  setDynamicsWebhookEnabled,
} from '@src/Api';

interface WebhookStatus {
  enabled: boolean;
  active: boolean;
  expirationDate: string | null;
  lastRenewedAt: string | null;
  lastError: string | null;
}

type WebhookType = 'customers' | 'contacts' | 'generalLedgerEntries';

const webhookActivators: Record<WebhookType, (force?: boolean) => ReturnType<typeof activateDynamicsCustomerWebhook>> = {
  customers: activateDynamicsCustomerWebhook,
  contacts: activateDynamicsContactWebhook,
  generalLedgerEntries: activateDynamicsLedgerWebhook,
};

const webhookDetails = {
  customers: { icon: People, description: 'Keep Rentman customers up to date when Business Central is your customer data master.' },
  contacts: { icon: Person, description: 'Update the related customer’s contact persons when their details change in Business Central.' },
  generalLedgerEntries: { icon: Receipt, description: 'Match payments when payment synchronization is enabled in Settings. Other postings are ignored.' },
};

const formatDate = (value: string) => new Date(value).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });

const WebhookSection: React.FC<{ type: WebhookType; label: string }> = ({ type, label }) => {
  const [status, setStatus] = React.useState<WebhookStatus | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [activating, setActivating] = React.useState(false);
  const [error, setError] = React.useState('');
  const [success, setSuccess] = React.useState('');

  const refreshStatus = async () => {
    const response = await getDynamicsWebhooks();
    if (!response || response.status !== 200 || !response.data?.[type]) {
      throw new Error('Unable to load Business Central webhook status.');
    }
    setStatus(response.data[type]);
  };

  React.useEffect(() => {
    refreshStatus()
      .catch(() => setError('Unable to load Business Central webhook status.'))
      .finally(() => setLoading(false));
  }, []);

  const activate = async () => {
    setActivating(true);
    setError('');
    setSuccess('');
    try {
      const response = await webhookActivators[type](true);
      await refreshStatus();
      if (!response || response.status !== 200) {
        throw new Error(typeof response?.data?.error === 'string' ? response.data.error : 'Unable to activate the webhook.');
      }
      setSuccess(`${label} webhook updated.`);
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Unable to activate the webhook.');
    } finally {
      setActivating(false);
    }
  };

  const setEnabled = async (enabled: boolean) => {
    setActivating(true);
    setError('');
    setSuccess('');
    try {
      const response = await setDynamicsWebhookEnabled(type, enabled);
      // Enabled intent is saved even if the immediate BC request fails.
      await refreshStatus();
      if (!response || response.status !== 200) throw new Error(response?.data?.error || 'Unable to update the webhook.');
      setSuccess(`${label} webhook ${enabled ? 'enabled' : 'disabled'}.`);
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Unable to update the webhook.');
    } finally {
      setActivating(false);
    }
  };

  const Icon = webhookDetails[type].icon;
  const statusLabel = loading
    ? 'Loading…'
    : !status
    ? 'Unavailable'
    : !status.enabled
    ? 'Disabled'
    : status.lastError
    ? 'Needs attention'
    : status.active
    ? 'Active'
    : 'Pending activation';
  const statusTone = !status || loading || !status.enabled ? 'neutral' : status.lastError ? 'warning' : status.active ? 'active' : 'pending';

  return (
    <section aria-label={`${label} webhook`} className="webhook-row">
      <div className="webhook-identity">
        <span className="webhook-icon">
          <Icon size={22} aria-hidden="true" />
        </span>
        <div>
          <h2>{label}</h2>
          <p className="webhook-description">{webhookDetails[type].description}</p>
        </div>
      </div>
      <div className="webhook-state">
        <span role="status" className={`webhook-status webhook-status--${statusTone}`}>
          Status: {statusLabel}
        </span>
        <dl className="webhook-dates">
          <div>
            <dt>Last renewed</dt>
            <dd>{status?.lastRenewedAt ? <time dateTime={status.lastRenewedAt}>{formatDate(status.lastRenewedAt)}</time> : '—'}</dd>
          </div>
          <div>
            <dt>Expires</dt>
            <dd>{status?.expirationDate ? <time dateTime={status.expirationDate}>{formatDate(status.expirationDate)}</time> : '—'}</dd>
          </div>
        </dl>
      </div>
      <div className="webhook-actions">
        <Form.Check
          type="switch"
          id={`webhook-${type}-enabled`}
          label="Enabled"
          checked={status?.enabled ?? false}
          disabled={loading || activating || !status}
          onChange={(event) => void setEnabled(event.target.checked)}
        />
        <Button variant="outline-secondary" size="sm" type="button" onClick={activate} disabled={loading || activating || !status?.enabled}>
          {activating ? (
            <Spinner as="span" animation="border" size="sm" className="me-2" aria-hidden="true" />
          ) : (
            <ArrowRepeat className="me-2" aria-hidden="true" />
          )}
          {activating ? 'Updating…' : 'Renew / repair'}
        </Button>
        {status && !status.enabled && status.lastError && (
          <Button size="sm" type="button" variant="outline-secondary" onClick={() => void setEnabled(false)} disabled={loading || activating}>
            Retry removal
          </Button>
        )}
      </div>
      {(error || status?.lastError || success) && (
        <div className="webhook-feedback">
          {error && <Alert variant="danger">{error}</Alert>}
          {status?.lastError && status.lastError !== error && <Alert variant="warning">{status.lastError}</Alert>}
          {success && (
            <p role="status" className="webhook-success">
              {success}
            </p>
          )}
        </div>
      )}
    </section>
  );
};

export const BusinessCentralWebhooksPage: React.FC = () => (
  <Row className="mt-4 webhooks-page">
    <Col sm={12}>
      <Card>
        <Card.Header>Business Central Webhooks</Card.Header>
        <Card.Body className="p-0">
          <WebhookSection type="customers" label="Customers" />
          <WebhookSection type="contacts" label="Contacts" />
          <WebhookSection type="generalLedgerEntries" label="General ledger entries" />
        </Card.Body>
      </Card>
    </Col>
  </Row>
);
